// ============================================================================
// TEAM MANAGEMENT HOOKS - Frontend
// React hooks for team management operations
// Feature 3: Team Management & Authorization
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listTeams,
  getTeam,
  createTeam,
  getTeamMembers,
  addTeamMember
} from '../services/teamService';
import {
  CreateTeamInput,
  AddTeamMemberInput
} from '../types/team.types';

// ==================== TEAM QUERIES ====================

export const useTeams = (options?: {
  status?: 'active' | 'archived';
  includeCounts?: boolean;
}) => {
  return useQuery({
    queryKey: ['teams', options?.status, options?.includeCounts],
    queryFn: () => listTeams(options),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useTeam = (teamId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['team', teamId],
    queryFn: () => getTeam(teamId),
    enabled: enabled && !!teamId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useTeamMembers = (teamId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['team-members', teamId],
    queryFn: () => getTeamMembers(teamId),
    enabled: enabled && !!teamId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// ==================== TEAM MUTATIONS ====================

export const useCreateTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateTeamInput) => createTeam(input),
    onSuccess: () => {
      // Invalidate teams list
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

// ==================== MEMBER MUTATIONS ====================

export const useAddTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ teamId, member }: { teamId: string; member: AddTeamMemberInput }) =>
      addTeamMember(teamId, member),
    onSuccess: (_, variables) => {
      // Invalidate team members and user teams
      queryClient.invalidateQueries({ queryKey: ['team-members', variables.teamId] });
      queryClient.invalidateQueries({ queryKey: ['user-teams', variables.member.userId] });
      queryClient.invalidateQueries({ queryKey: ['teams'] }); // For member counts
    },
  });
};

